import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Users, Plus } from 'lucide-react';
import { Panel, Field, FormRow, fmtDate } from './design';
import { useConfirm } from '../../components/ConfirmDialog';

// Registered supporters and what is shared with them.
//
// A supporter with consent to share (in force today) receives the monthly statement, by email,
// letter or both, and can be given family portal access. Supporter (lite) is recorded without
// statements by default. A decision maker (guardian, enduring power of attorney) is recorded on the
// person. Every statement and invitation sent to a supporter is in the sharing log.

const TYPES = [['supporter', 'Registered supporter'], ['supporter_lite', 'Supporter (lite)'], ['guardian', 'Supporter (guardian)']];
const BY = [['email', 'Email'], ['letter', 'Letter'], ['both', 'Email and letter'], ['none', 'Does not receive statements']];
const INP = 'input-field';
const EMPTY = { name: '', relationship: '', type: 'supporter', decisionMaker: false, decisionMakerBasis: '', email: '', phone: '', address: '',
  shareConsent: true, consentDate: '', consentUntil: '', statementsBy: 'email', notes: '' };
const d = (v) => (v ? String(v).slice(0, 10) : '');
const fromRow = (r) => ({ name: r.name || '', relationship: r.relationship || '', type: r.type || 'supporter', decisionMaker: !!r.decision_maker,
  decisionMakerBasis: r.decision_maker_basis || '', email: r.email || '', phone: r.phone || '', address: r.address || '',
  shareConsent: !!r.share_consent, consentDate: d(r.consent_date), consentUntil: d(r.consent_until), statementsBy: r.statements_by || 'email', notes: r.notes || '' });

export default function Supporters({ plan, onSaved }) {
  const clientId = plan.client_id;
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [form, setForm] = useState(null);   // { id?, ...fields }
  const [saving, setSaving] = useState(false);
  const [pref, setPref] = useState(plan.statement_delivery || 'email');
  const [loadError, setLoadError] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(() => {
    if (!clientId) return;
    setLoadError(null);
    api.get('/aged-care/supporters', { params: { client_id: clientId } }).then((r) => setRows(r.data || []))
      .catch((e) => { setRows([]); setLoadError(e.response?.data?.message || 'Could not load the supporters'); });
    api.get('/aged-care/sharing-log', { params: { client_id: clientId } }).then((r) => setLog(r.data || [])).catch(() => setLog([]));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPref(plan.statement_delivery || 'email'); }, [plan.statement_delivery]);

  const savePref = async (v) => {
    setPref(v);
    try { await api.patch(`/aged-care/funding/${plan.id}`, { statementDelivery: v }); toast.success('Saved'); if (onSaved) onSaved(); }
    catch { toast.error('Could not save'); }
  };
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    if (!form.name.trim()) return toast.error('Enter their name');
    setSaving(true);
    try {
      const body = { ...form, clientId };
      if (form.id) await api.put(`/aged-care/supporters/${form.id}`, body); else await api.post('/aged-care/supporters', body);
      toast.success('Supporter saved'); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  };
  const remove = async (r) => {
    const ok = await confirm(`${r.name} stops receiving statements and anything else shared with supporters. Their record and the sharing log are kept.`, { title: 'Remove this supporter?', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await api.put(`/aged-care/supporters/${r.id}`, { active: false }); toast.success('No longer a supporter'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not update'); }
  };
  const invite = async (r) => {
    try {
      const res = await api.post('/aged-care-portal/accounts', { client_id: clientId, supporter_id: r.id });
      toast.success(res.data?.emailed === false ? 'Access created, but the invitation email failed' : `Portal invitation sent to ${r.email}`); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not give access'); }
  };
  const label = (list, v) => (list.find(([k]) => k === v) || [, v])[1];

  return (
    <Panel title="Supporters and statements" icon={Users} bodyClass="px-4 py-3 space-y-3"
      actions={!form && <button type="button" onClick={() => setForm({ ...EMPTY })} className="btn-secondary btn-sm"><Plus size={13} /> Add supporter</button>}>
      {loadError && (
        <p className="text-[13px] text-red-800">{loadError}. <button type="button" className="font-medium underline" onClick={load}>Try again</button></p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-slate-700">
        <span>{plan.client_name || 'The participant'} receives their statement by</span>
        <select aria-label="Participant statement delivery" className={`${INP} !w-auto !py-1`} value={pref} onChange={(e) => savePref(e.target.value)}>
          <option value="email">Email</option><option value="letter">Letter</option><option value="both">Email and letter</option>
        </select>
        <span className="text-[12px] text-slate-500">With no email address on file, it goes by letter.</span>
      </div>
      {rows.filter((r) => r.active !== false).map((r) => {
        const inForce = r.share_consent && (!r.consent_until || d(r.consent_until) >= new Date().toISOString().slice(0, 10));
        return (
          <div key={r.id} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px]">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium text-slate-900">{r.name}{r.relationship ? <span className="font-normal text-slate-500"> · {r.relationship}</span> : null}</span>
              <span className="flex gap-1.5">
                <button type="button" className="btn-ghost btn-sm" onClick={() => setForm({ id: r.id, ...fromRow(r) })}>Edit</button>
                {inForce && r.email && <button type="button" className="btn-ghost btn-sm" onClick={() => invite(r)}>Give portal access</button>}
                <button type="button" className="btn-ghost btn-sm" onClick={() => remove(r)}>Remove</button>
              </span>
            </div>
            <p className="text-slate-600">
              {label(TYPES, r.type)}{r.decision_maker ? ` · decision maker${r.decision_maker_basis ? ` (${r.decision_maker_basis})` : ''}` : ''}
              {' · '}{inForce ? `consent to share${r.consent_until ? ` until ${fmtDate(r.consent_until)}` : ''}` : <span className="text-amber-800">no consent to share in force</span>}
              {' · '}statements: {inForce ? label(BY, r.statements_by) : 'not sent'}
            </p>
          </div>
        );
      })}
      {form && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3">
          <FormRow cols={2}>
            <Field label="Name" required>{(id) => <input id={id} className={INP} value={form.name} onChange={set('name')} />}</Field>
            <Field label="Relationship">{(id) => <input id={id} className={INP} value={form.relationship} onChange={set('relationship')} placeholder="Daughter" />}</Field>
          </FormRow>
          <FormRow cols={2}>
            <Field label="Type">{(id) => <select id={id} className={INP} value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value, statementsBy: e.target.value === 'supporter_lite' && !p.id ? 'none' : p.statementsBy }))}>{TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}</Field>
            <Field label="Statements">{(id) => <select id={id} className={INP} value={form.statementsBy} onChange={set('statementsBy')}>{BY.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}</Field>
          </FormRow>
          <FormRow cols={2}>
            <Field label="Email">{(id) => <input id={id} type="email" className={INP} value={form.email} onChange={set('email')} />}</Field>
            <Field label="Phone">{(id) => <input id={id} className={INP} value={form.phone} onChange={set('phone')} />}</Field>
          </FormRow>
          <Field label="Postal address" hint="Needed for statements by letter.">{(id) => <input id={id} className={INP} value={form.address} onChange={set('address')} placeholder="Street, suburb, state, postcode" />}</Field>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slate-700">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.shareConsent} onChange={set('shareConsent')} /> Consent to share information</label>
            <label className="flex items-center gap-1.5">From <input type="date" className={`${INP} !w-auto !py-1`} value={form.consentDate} onChange={set('consentDate')} /></label>
            <label className="flex items-center gap-1.5">Until <input type="date" className={`${INP} !w-auto !py-1`} value={form.consentUntil} onChange={set('consentUntil')} /></label>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slate-700">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.decisionMaker} onChange={set('decisionMaker')} /> Appointed decision maker</label>
            {form.decisionMaker && <input aria-label="Basis" className={`${INP} !w-72 !py-1`} value={form.decisionMakerBasis} onChange={set('decisionMakerBasis')} placeholder="For example: enduring guardian, NSW" />}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost btn-sm" onClick={() => setForm(null)}>Cancel</button>
            <button type="button" className="btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving' : 'Save supporter'}</button>
          </div>
        </div>
      )}
      <button type="button" className="btn-ghost btn-sm" onClick={() => setShowLog((v) => !v)}>{showLog ? 'Hide' : 'Show'} what has been shared ({log.length})</button>
      {showLog && (
        <ul className="text-[12px] text-slate-600 space-y-0.5">
          {log.map((l) => <li key={l.id}>{fmtDate(l.at)} · {l.supporter_name || 'Supporter'} · {l.what === 'statement' ? `statement by ${l.how}` : l.what === 'portal_access' ? 'family portal invitation' : l.detail}{l.what === 'statement' && l.detail ? ` (${l.detail})` : ''}{l.by_name ? ` · ${l.by_name}` : ''}</li>)}
          {!log.length && <li>Nothing shared yet.</li>}
        </ul>
      )}
    </Panel>
  );
}
